import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usersAPI } from '../services/api';
import { Store, TrendingUp, DollarSign, Users, ShoppingBag } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin');
      return;
    }
    usersAPI.getMe().then((res) => setProfile(res.data)).catch(() => {});
  }, [user, navigate]);

  if (!profile) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const isGestor = profile.role === 'gestor';

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            Bienvenido, {profile.first_name}
          </h1>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            STTORA ID: {profile.sttora_id} · {profile.role === 'gestor' ? 'Gestor Independiente' : 'Cliente Premium'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isGestor && (
            <>
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <DollarSign className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-sm text-gray-500">Comisiones</p>
                <p className="text-2xl font-bold">${profile.total_commission?.toFixed(2) || '0.00'}</p>
              </div>
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <DollarSign className="w-8 h-8 text-blue-500 mb-3" />
                <p className="text-sm text-gray-500">Saldo Disponible</p>
                <p className="text-2xl font-bold">${profile.available_balance?.toFixed(2) || '0.00'}</p>
              </div>
            </>
          )}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <TrendingUp className="w-8 h-8 text-purple-500 mb-3" />
            <p className="text-sm text-gray-500">Rango</p>
            <p className="text-2xl font-bold">{profile.rank || '—'}</p>
          </div>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <ShoppingBag className="w-8 h-8 text-orange-500 mb-3" />
            <p className="text-sm text-gray-500">Ventas</p>
            <p className="text-2xl font-bold">${profile.total_sales?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/store"
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} hover:shadow-md transition-shadow flex items-center gap-4`}>
            <div className="p-4 bg-indigo-100 dark:bg-indigo-900 rounded-2xl">
              <Store className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Tienda STTORA</h3>
              <p className="text-sm text-gray-500">Compra productos y acumula puntos</p>
            </div>
          </Link>
          <Link to="/orders"
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} hover:shadow-md transition-shadow flex items-center gap-4`}>
            <div className="p-4 bg-green-100 dark:bg-green-900 rounded-2xl">
              <ShoppingBag className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Mis Órdenes</h3>
              <p className="text-sm text-gray-500">Revisa el estado de tus compras</p>
            </div>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;
